import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import './TemplatePage.css';

// ==========================================
// LAYOUTS
// ==========================================
import AdminLayout from './_admin/AdminLayout';
import PCLayout from './_public_pc/PCLayout';
import MovilLayout from './_public_movil/MovilLayout';
import PendienteModal from './PendienteModal';

// ==========================================
// CONSTANTES
// ==========================================
const API_BASE = process.env.REACT_APP_API_URL || '/api';

// ==========================================
// FUNCIÓN PARA OBTENER UUID
// ==========================================
const getOrCreateUUID = () => {
    try {
        let uuid = localStorage.getItem('user_uuid');
        if (!uuid) {
            uuid = crypto.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).substring(2)}`;
            localStorage.setItem('user_uuid', uuid);
        }
        return uuid;
    } catch (error) {
        console.error('Error al obtener UUID:', error);
        return `temp-${Date.now()}-${Math.random().toString(36).substring(2)}`;
    }
};

// ==========================================
// HOOK PERSONALIZADO PARA DATOS DEL TEMPLATE
// ==========================================
const useTemplateData = (slug) => {
    const [data, setData] = useState({
        template: null,
        colors: null,
        isLoading: true,
        error: null
    });

    useEffect(() => {
        if (!slug) return;

        const fetchAll = async () => {
            try {
                const [templateRes, colorsRes] = await Promise.all([
                    axios.get(`${API_BASE}/admin/template/${slug}`),
                    axios.get(`${API_BASE}/colors/${slug}`)
                ]);

                setData({
                    template: templateRes.data,
                    colors: colorsRes.data,
                    isLoading: false,
                    error: null
                });
            } catch (error) {
                console.error('Error al cargar datos:', error);
                setData(prev => ({
                    ...prev,
                    isLoading: false,
                    error: error.response?.data?.message || error.message || 'Error al cargar el evento'
                }));
            }
        };

        fetchAll();
    }, [slug]);

    return data;
};

// ==========================================
// HOOK PERSONALIZADO PARA PAGO PENDIENTE
// ==========================================
const usePendientePago = () => {
    const [estado, setEstado] = useState({
        showModal: false,
        data: null,
        verificando: false,
        mensaje: null
    });
    const [cooldown, setCooldown] = useState(0);
    const [intentos, setIntentos] = useState(0);
    const MAX_INTENTOS = 3;

    // Contador decreciente para el cooldown
    useEffect(() => {
        if (cooldown <= 0) return;
        const timer = setInterval(() => {
            setCooldown(prev => prev - 1);
        }, 1000);
        return () => clearInterval(timer);
    }, [cooldown]);

    const abrirModalConDatos = useCallback((data) => {
        setEstado({
            showModal: true,
            data: data,
            verificando: false,
            mensaje: null
        });
        setIntentos(0);
        setCooldown(0);
    }, []);

    // Función de verificación real contra la API
    const verificarPendiente = useCallback(async () => {
        const yaCancelado = localStorage.getItem('pendiente_cancelado') === 'true';
        if (yaCancelado) return;

        const uuid = getOrCreateUUID();
        try {
            const response = await axios.get(
                `${API_BASE}/preinscripcion/status/${uuid}`,
                {
                    headers: { 'Cache-Control': 'no-cache' },
                    params: { _t: Date.now() } // cache-busting adicional
                }
            );
            
            if (response.data.tienePendiente) {
                setEstado({
                    showModal: true,
                    data: {
                        qrId: response.data.qrId,
                        competenciaSlug: response.data.competencia_slug,
                        fechaCreacion: response.data.fecha_creacion
                    },
                    verificando: false,
                    mensaje: null
                });
            }
        } catch (error) {
            if (error.response?.status !== 404) {
                console.error('[Pendiente] Error verificando:', error);
            }
        }
    }, []);

    const handleVerificar = useCallback(async () => {
        if (!estado.data || estado.verificando || cooldown > 0) return;

        if (intentos >= MAX_INTENTOS) {
            setEstado(prev => ({
                ...prev,
                mensaje: { 
                    type: 'error', 
                    text: 'Superaste el límite de verificaciones. Si realizaste el pago por favor contacta al organizador del evento.' 
                }
            }));
            return;
        }

        const nuevoIntento = intentos + 1;
        setIntentos(nuevoIntento);
        setEstado(prev => ({ ...prev, verificando: true, mensaje: null }));

        try {
            const response = await axios.post(`${API_BASE}/preinscripcion/verificar-pago-manual`, {
                id_qr: estado.data.qrId
            });

            if (response.data.status === 'completado') {
                localStorage.removeItem('user_uuid');
                localStorage.removeItem('pendiente_cancelado');
                setEstado(prev => ({
                    ...prev,
                    mensaje: { type: 'success', text: '¡Pago confirmado!' },
                    verificando: false
                }));
            } else {
                const quedan = MAX_INTENTOS - nuevoIntento;
                const msgText = quedan > 0 
                    ? `Transferencia no detectada aún. Te quedan ${quedan} intento${quedan > 1 ? 's' : ''}.`
                    : 'Límite de verificaciones alcanzado. Si ya pagaste, contacta al organizador por favor.';
                setEstado(prev => ({
                    ...prev,
                    mensaje: { type: quedan > 0 ? 'warning' : 'error', text: msgText },
                    verificando: false
                }));
                if (quedan > 0) setCooldown(30);
            }
        } catch (error) {
            const quedan = MAX_INTENTOS - nuevoIntento;
            const mensajeErr = quedan > 0 
                ? `No pudimos consultar con el banco. Intenta en unos momentos (te quedan ${quedan} intento${quedan > 1 ? 's' : ''}).`
                : 'Límite de verificaciones alcanzado. Si realizaste el pago por favor contacta al organizador.';
            setEstado(prev => ({
                ...prev,
                mensaje: { type: 'error', text: mensajeErr },
                verificando: false
            }));
            if (quedan > 0) setCooldown(30);
        }
    }, [estado.data, estado.verificando, cooldown, intentos]);

    const handleCancelar = useCallback(async () => {
        setEstado(prev => ({ showModal: false, data: null, verificando: false, mensaje: null }));
        localStorage.removeItem('user_uuid');
        localStorage.setItem('pendiente_cancelado', 'true');
        setTimeout(() => window.location.reload(), 300);
    }, []);

    const cerrarModal = useCallback(() => {
        setEstado(prev => ({ ...prev, showModal: false, data: null }));
    }, []);

    return {
        estado,
        cooldown,
        intentosMaximos: intentos >= MAX_INTENTOS,
        verificarPendiente,
        handleVerificar,
        handleCancelar,
        cerrarModal,
        abrirModalConDatos
    };
};

// ==========================================
// COMPONENTE PRINCIPAL
// ==========================================
const TemplatePage = ({ slug: propSlug, viewMode = false }) => {
    const { slug: urlSlug } = useParams();
    const slug = propSlug || urlSlug;

    // 1. Estado de dispositivo
    const [isMobile, setIsMobile] = useState(
        typeof window !== 'undefined' ? window.innerWidth <= 768 : false
    );
    
    // 2. Estado de admin
    const [isAdmin] = useState(() => {
        const role = localStorage.getItem('role');
        return role === 'Administrador' || role === 'Superadministrador';
    });

    // 3. Hook para datos del template
    const { template, colors, isLoading, error } = useTemplateData(slug);

    // 4. Hook para pago pendiente
    const { 
        estado: pendiente, 
        cooldown,
        intentosMaximos,
        verificarPendiente, 
        handleVerificar, 
        handleCancelar, 
        cerrarModal 
    } = usePendientePago();

    // 5. useMemo para metadata
    const metadata = useMemo(() => {
        const title = template?.slug || 'Evento Deportivo';
        const description = template?.description || 'Evento deportivo';
        const image = template?.banner?.logo || '';
        return { title, description, image };
    }, [template]);

    // 6. useMemo para determinar layout
    const Layout = useMemo(() => {
        if (isAdmin) return AdminLayout;
        if (isMobile) return MovilLayout;
        return PCLayout;
    }, [isAdmin, isMobile]);

    // ==========================================
    // EFECTOS
    // ==========================================

    // 7. Detección de dispositivo
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // 8. Verificar pago pendiente real — independiente de si el template
    // ya terminó de cargar, para no depender de esa carrera de tiempos.
    useEffect(() => {
        if (!isAdmin) {
            verificarPendiente();
        }
    }, [isAdmin, verificarPendiente]);

    // ==========================================
    // RENDER: LOADING
    // ==========================================
    if (isLoading) {
        return (
            <>
                <Helmet>
                    <title>Cargando evento... | InovateSport</title>
                </Helmet>
                <div className="template-loading-container">
                    <div className="modern-spinner">
                        <div className="spinner-ring"></div>
                        <div className="spinner-ring"></div>
                        <div className="spinner-ring"></div>
                    </div>
                    <p className="loading-text">Preparando la experiencia del evento...</p>
                </div>
            </>
        );
    }
  
    // ==========================================
    // RENDER: ERROR / 404
    // ==========================================
    if (error || !template) {
        const isNotFound = error?.response?.status === 404;
        
        return (
            <>
                <Helmet>
                    <title>{isNotFound ? 'Evento no encontrado' : 'Error'} | InovateSport</title>
                </Helmet>
                <div className="template-error-container">
                    <div className="error-visual">
                        {isNotFound ? (
                            <>
                                <span className="error-emoji">🔍</span>
                                <h2>¡Ups! Parece que te equivocaste de evento</h2>
                                <p>
                                    El evento que buscas no existe, ha sido eliminado o la dirección web es incorrecta. 
                                    No te preocupes, puedes explorar todos nuestros eventos activos.
                                </p>
                                <a href="/Eventos" className="error-action-btn primary">
                                    🏃‍♂️ Ir a la lista de eventos
                                </a>
                                <button 
                                    className="error-action-btn secondary"
                                    onClick={() => window.location.href = '/'}
                                >
                                    🏠 Volver al inicio
                                </button>
                            </>
                        ) : (
                            <>
                                <span className="error-emoji">⚠️</span>
                                <h2>Algo salió mal</h2>
                                <p>{error?.response?.data?.message || error?.message || 'No pudimos cargar el evento en este momento.'}</p>
                                <button 
                                    className="error-action-btn primary"
                                    onClick={() => window.location.reload()}
                                >
                                    🔄 Reintentar
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </>
        );
    }

    // ==========================================
    // RENDER PRINCIPAL
    // ==========================================
    return (
        <>
            <Helmet>
                <title>{metadata.title}</title>
                <meta property="og:title" content={metadata.title} />
                <meta property="og:description" content={metadata.description} />
                {metadata.image && <meta property="og:image" content={metadata.image} />}
                <meta name="viewport" content="width=device-width, initial-scale=1.0" />
            </Helmet>

            {pendiente.showModal && (
                <PendienteModal
                    data={pendiente.data}
                    onVerificar={handleVerificar}
                    onCancelar={handleCancelar}
                    onClose={cerrarModal}
                    isLoading={pendiente.verificando}
                    mensaje={pendiente.mensaje}
                    cooldown={cooldown}
                    intentosMaximos={intentosMaximos}
                />
            )}

            <Layout
                slug={slug}
                viewMode={viewMode}
                colors={colors}
                templateData={template}
            />
        </>
    );
};

export default TemplatePage;